// v2/pages/admin/AdminDebtV2.js — Yonetim / Borc Yaslandirma (/v2/admin/debt)
// URL: ?q= &risk= &debtors= &overdue= &restricted= &ruled= &sort= &dir= &page= &per=
// Hesap eski sistemle AYNI (FIFO). Senkron yalnizca "Senkronize Et" ile calisir.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Wallet, ShieldAlert, ShieldCheck, Search, X, SlidersHorizontal, RotateCw, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, ChevronsUpDown, AlertTriangle, Inbox, Loader2, Users, CircleDollarSign, Clock, Info, CheckCircle2, FileSpreadsheet,
} from 'lucide-react';
import useAdminDebt from '../../hooks/useAdminDebt';
import { adminDebtApi } from '../../hooks/useAdminDebt';
import { dosyaIndir } from '../../hooks/useQuotes';
import { qs } from '../../utils/api';
import RestrictionModal from '../../components/admin/RestrictionModal';
import ToastStack from '../../components/Toast';
import useToast from '../../hooks/useToast';
import { formatTRY, formatTRYShort, formatNumber, formatPercent } from '../../utils/format';
import { SkelPanel } from '../../components/Skeleton';
import s from '../../styles/AdminDebtV2.module.css';
import '../../styles/tokens.css';

const DILIMLER = [
  { id: '0-30', label: '0-30 Gün', sort: 'd0', cls: 'bkOk' },
  { id: '31-60', label: '31-60 Gün', sort: 'd30', cls: 'bkWatch' },
  { id: '61-90', label: '61-90 Gün', sort: 'd60', cls: 'bkWarn' },
  { id: '91-120', label: '91-120 Gün', sort: 'd90', cls: 'bkDanger' },
  { id: '121-150', label: '121-150 Gün', sort: 'd120', cls: 'bkSevere' },
  { id: '151-180', label: '151-180 Gün', sort: 'd150', cls: 'bkCrit' },
  { id: '180+', label: '180+ Gün', sort: 'd180', cls: 'bkExtreme' },
];
const ALANLAR = {
  '0-30': 'gun_0_30', '31-60': 'gun_31_60', '61-90': 'gun_61_90', '91-120': 'gun_91_120',
  '121-150': 'gun_121_150', '151-180': 'gun_151_180', '180+': 'gun_180_ustu',
};
const RISKLER = [
  { id: '', label: 'Tüm riskler' },
  { id: 'cok_kritik', label: 'Çok kritik (180+ gün)' },
  { id: 'cok_ciddi', label: 'Çok ciddi (151-180)' },
  { id: 'ciddi', label: 'Ciddi (121-150)' },
  { id: 'kritik', label: 'Kritik (91-120)' },
  { id: 'yuksek', label: 'Yüksek (61-90)' },
  { id: 'orta', label: 'Orta (31-60)' },
  { id: 'dusuk', label: 'Düşük (0-30)' },
  { id: 'temiz', label: 'Temiz (borç yok)' },
];
const RISK_ETIKET = {
  cok_kritik: { label: 'Çok kritik', cls: 'rkExtreme' },
  cok_ciddi: { label: 'Çok ciddi', cls: 'rkVSevere' },
  ciddi: { label: 'Ciddi', cls: 'rkSevere' },
  kritik: { label: 'Kritik', cls: 'rkCrit' },
  yuksek: { label: 'Yüksek', cls: 'rkHigh' },
  orta: { label: 'Orta', cls: 'rkMed' },
  dusuk: { label: 'Düşük', cls: 'rkLow' },
  temiz: { label: 'Temiz', cls: 'rkClean' },
};
const ANAHTARLAR = [
  { id: 'debtors', label: 'Sadece borçlular', ikon: CircleDollarSign },
  { id: 'overdue', label: 'Vadesi geçmiş', ikon: Clock },
  { id: 'restricted', label: 'Kısıtlı', ikon: ShieldAlert },
  { id: 'ruled', label: 'Kuralı olan', ikon: ShieldCheck },
];
const VARSAYILAN = { sort: 'balance', dir: 'desc', page: '1', per: '25' };

const tarihTR = (iso) => {
  if (!iso) return '-';
  const [y, a, g] = String(iso).slice(0, 10).split('-');
  return y ? `${g}.${a}.${y}` : '-';
};

/** Kisitlama rozeti. bloke/kalan_saat sunucudan gelir (tarayici saatine guvenilmez). */
const sonSaatler = (k) => k.kalan_saat !== null && k.kalan_saat !== undefined && k.kalan_saat < 24;

function kisitEtiket(k, mobil) {
  if (!k) return mobil ? 'Kısıtla' : 'Ekle';
  if (!k.aktif) return 'Kural var';
  if (k.bloke) return 'Kısıtlı';
  if (sonSaatler(k)) return k.kalan_saat < 1 ? '1 saatten az' : `${k.kalan_saat} saat${mobil ? ' kaldı' : ''}`;
  if (k.kalan_gun === null || k.kalan_gun === undefined) return 'Kısıtlı';
  return `${k.kalan_gun} gün${mobil ? ' kaldı' : ''}`;
}

function kisitSinif(s, k) {
  if (!k) return '';
  if (!k.aktif) return s.kisitRule;
  if (k.bloke) return `${s.kisitOn} ${s.kisitBloke}`;
  if (sonSaatler(k)) return `${s.kisitOn} ${s.kisitSaat}`;
  return s.kisitOn;
}

export default function AdminDebtV2() {
  const [sp, setSp] = useSearchParams();
  const oku = useCallback((k) => sp.get(k) || VARSAYILAN[k] || '', [sp]);
  const { items: toastlar, push, dismiss } = useToast();

  const [arama, setArama] = useState(sp.get('q') || '');
  const [filtreAcik, setFiltreAcik] = useState(false);
  const [modalMusteri, setModalMusteri] = useState(null);
  const [senkronda, setSenkronda] = useState(false);

  useEffect(() => { setArama(sp.get('q') || ''); }, [sp]);

  const params = useMemo(() => ({
    q: sp.get('q') || '', risk: sp.get('risk') || '',
    debtors: sp.get('debtors') || '', overdue: sp.get('overdue') || '',
    restricted: sp.get('restricted') || '', ruled: sp.get('ruled') || '',
    sort: oku('sort'), dir: oku('dir'), page: oku('page'), per: oku('per'),
  }), [sp, oku]);

  const { data, loading, error, yenile } = useAdminDebt(params);
  const [excelde, setExcelde] = useState(false);
  const excelAktar = async () => {
    if (excelde) return;
    setExcelde(true);
    try {
      const { page, per, ...filtre } = params;
      const temiz = Object.fromEntries(Object.entries(filtre).filter(([, v]) => v));
      await dosyaIndir(`/v2/admin/debt/export/${qs(temiz)}`, 'Borc_Yaslandirma.xlsx');
    } catch (e) {
      push(e.message || 'Excel oluşturulamadı', 'error');
    } finally {
      setExcelde(false);
    }
  };

  const guncelle = useCallback((yeni, sayfaSifirla = true) => {
    setSp((onceki) => {
      const p = new URLSearchParams(onceki);
      Object.entries(yeni).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined || v === VARSAYILAN[k]) p.delete(k);
        else p.set(k, v);
      });
      if (sayfaSifirla) p.delete('page');
      return p;
    }, { replace: true });
  }, [setSp]);

  useEffect(() => {
    const suAn = sp.get('q') || '';
    if (arama === suAn) return undefined;
    const t = setTimeout(() => guncelle({ q: arama.trim() }), 400);
    return () => clearTimeout(t);
  }, [arama, sp, guncelle]);

  const sirala = (alan) => {
    if (!alan) return;
    const ayni = params.sort === alan;
    guncelle({ sort: alan, dir: ayni && params.dir === 'desc' ? 'asc' : 'desc' });
  };

  const senkronla = useCallback(async () => {
    if (senkronda) return;
    setSenkronda(true);
    try {
      const v = await adminDebtApi.senkron();
      if (!v || !v.success) throw new Error((v && v.message) || 'Senkron başarısız');
      push(v.message, v.skipped ? 'error' : 'success');
      if (!v.skipped) yenile();
    } catch (e) {
      push((e && e.message) || 'Senkron başarısız', 'error');
    } finally {
      setSenkronda(false);
    }
  }, [senkronda, push, yenile]);

  const kuralKaydedildi = useCallback((mesaj) => {
    setModalMusteri(null);
    push(mesaj, 'success');
    yenile();
  }, [push, yenile]);

  const ilk = loading && !data;
  const ogeler = (data && data.items) || [];
  const f = (data && data.filtered) || {};
  const genel = (data && data.summary) || {};
  const riskler = (data && data.risk_counts) || {};
  const kritikUstu = ['kritik', 'ciddi', 'cok_ciddi', 'cok_kritik']
    .reduce((a, k) => a + (Number(riskler[k]) || 0), 0);
  const sayfa = (data && data.page) || 1;
  const sonSayfa = (data && data.total_pages) || 1;
  const toplam = (data && data.total) || 0;
  const per = Number(params.per) || 25;
  const ilkSira = (sayfa - 1) * per;
  const acikFiltre = ['risk', 'debtors', 'overdue', 'restricted', 'ruled']
    .filter((k) => params[k]).length;

  const dilimToplam = DILIMLER.reduce((a, d) => {
    const alan = ALANLAR[d.id];
    return a + (Number(f[alan]) || 0);
  }, 0);

  const SiraIkon = ({ alan }) => {
    if (!alan) return null;
    if (params.sort !== alan) return <ChevronsUpDown size={13} className={s.sortIdle} />;
    return params.dir === 'asc'
      ? <ChevronUp size={13} className={s.sortOn} />
      : <ChevronDown size={13} className={s.sortOn} />;
  };

  return (
    <div className={s.page}>
      <div className={s.head}>
        <div className={s.headText}>
          <h2 className={s.title}>Borç Yaşlandırma</h2>
          <p className={s.subtitle}>
            {ilk ? 'Yükleniyor…' : (
              <>
                <b>{formatNumber(toplam)}</b> cari · FIFO yöntemi ·
                {' '}<b>{formatNumber(genel.borclu_musteri_sayisi)}</b> borçlu,
                {' '}{formatNumber(genel.temiz_musteri_sayisi)} temiz
              </>
            )}
          </p>
        </div>
        <div className={s.headBtns}>
          <button type="button" className={`${s.ghostBtn} ${s.excelBtn}`} onClick={excelAktar}
            disabled={excelde || loading} title="Seçili filtre ve sıralamaya göre tüm cariler">
            {excelde ? <Loader2 size={15} className={s.excelSpin} /> : <FileSpreadsheet size={15} />}
            <span>{excelde ? 'Hazırlanıyor…' : 'Excel\'e Aktar'}</span>
          </button>
          <button type="button" className={s.ghostBtn} onClick={senkronla} disabled={senkronda || loading}
            title="Kısıtlamaları FIFO'ya göre yeniden hesaplar">
            <RefreshCw size={15} className={senkronda ? s.spin : ''} />
            <span className={s.btnText}>{senkronda ? 'Senkronlanıyor' : 'Senkronize Et'}</span>
          </button>
          <button type="button" className={s.ghostBtn} onClick={yenile} disabled={loading}>
            <RotateCw size={15} className={loading ? s.spin : ''} />
            <span className={s.btnText}>Yenile</span>
          </button>
        </div>
      </div>

      <div className={s.stats}>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tRed}`}><Wallet size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatTRYShort(f.net_bakiye)}</span>
            <span className={s.statLabel}>Toplam alacak</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tAmber}`}><Clock size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatTRYShort(f.vadesi_gecmis)}</span>
            <span className={s.statLabel}>
              Vadesi geçmiş (31+ gün)
              {f.net_bakiye ? ` · ${formatPercent((f.vadesi_gecmis / f.net_bakiye) * 100, 1)}` : ''}
            </span>
          </span>
        </div>
        <button type="button"
          className={`${s.stat} ${s.statBtn} ${params.restricted === '1' ? s.statOn : ''}`}
          onClick={() => guncelle({ restricted: params.restricted === '1' ? '' : '1' })}>
          <span className={`${s.statIcon} ${s.tViolet}`}><ShieldAlert size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(f.kisitli)}</span>
            <span className={s.statLabel}>Aktif kısıtlama · {formatNumber(f.kurali_olan)} kural</span>
          </span>
        </button>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tBlue}`}><Users size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(kritikUstu)}</span>
            <span className={s.statLabel}>
              91+ gün · {formatNumber(riskler.cok_kritik)} çok kritik · {formatNumber(riskler.yuksek)} yüksek
            </span>
          </span>
        </div>
      </div>

      <section className={s.bucketBar}>
        <div className={s.bucketHead}>
          <h3 className={s.bucketTitle}>Yaşlandırma Dağılımı</h3>
          <span className={s.bucketTotal}>{formatTRY(dilimToplam)}</span>
        </div>
        <div className={s.bucketTrack}>
          {DILIMLER.map((d) => {
            const alan = ALANLAR[d.id];
            const v = Number(f[alan]) || 0;
            const oran = dilimToplam > 0 ? (v / dilimToplam) * 100 : 0;
            if (oran <= 0) return null;
            return (
              <span key={d.id} className={`${s.bucketSeg} ${s[d.cls]}`} style={{ width: `${oran}%` }}
                title={`${d.label}: ${formatTRY(v)}`} />
            );
          })}
        </div>
        <div className={s.bucketLegend}>
          {DILIMLER.map((d) => {
            const alan = ALANLAR[d.id];
            const v = Number(f[alan]) || 0;
            return (
              <span key={d.id} className={s.legendItem}>
                <i className={`${s.legendDot} ${s[d.cls]}`} />
                <span className={s.legendLabel}>{d.label}</span>
                <b className={s.legendValue}>{formatTRYShort(v)}</b>
              </span>
            );
          })}
        </div>
      </section>

      <div className={s.toolbar}>
        <div className={s.searchBox}>
          <Search size={16} className={s.searchIcon} />
          <input type="search" className={s.search} placeholder="Müşteri kodu veya ünvanı…"
            value={arama} onChange={(e) => setArama(e.target.value)} aria-label="Müşteri ara" />
          {arama && (
            <button type="button" className={s.clearBtn} onClick={() => setArama('')} aria-label="Temizle">
              <X size={15} />
            </button>
          )}
        </div>
        <button type="button" className={`${s.ghostBtn} ${filtreAcik ? s.ghostOn : ''}`}
          onClick={() => setFiltreAcik((v) => !v)} aria-expanded={filtreAcik}>
          <SlidersHorizontal size={15} />
          <span className={s.btnText}>Filtreler</span>
          {acikFiltre > 0 && <span className={s.badge}>{acikFiltre}</span>}
        </button>
      </div>

      {filtreAcik && (
        <div className={s.filters}>
          <label className={s.field}>
            <span className={s.fieldLabel}>Risk</span>
            <select className={s.select} value={params.risk}
              onChange={(e) => guncelle({ risk: e.target.value })}>
              {RISKLER.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}{r.id && riskler[r.id] !== undefined ? ` (${riskler[r.id]})` : ''}
                </option>
              ))}
            </select>
          </label>
          <div className={s.toggles}>
            {ANAHTARLAR.map((a) => {
              const Ikon = a.ikon;
              const acik = params[a.id] === '1';
              return (
                <button key={a.id} type="button"
                  className={`${s.toggle} ${acik ? s.toggleOn : ''}`}
                  onClick={() => guncelle({ [a.id]: acik ? '' : '1' })} aria-pressed={acik}>
                  <Ikon size={14} /> {a.label}
                </button>
              );
            })}
            {(acikFiltre > 0 || params.q) && (
              <button type="button" className={s.resetBtn} onClick={() => setSp({}, { replace: true })}>
                <X size={14} /> Temizle
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className={s.alert}>
          <AlertTriangle size={18} /> <span>{error}</span>
          <button type="button" className={s.alertBtn} onClick={yenile}>Tekrar dene</button>
        </div>
      )}

      <section className={s.panel}>
        {loading && !ilk && <span className={s.loadBar} aria-hidden="true" />}
        {ilk ? (
          <div className={s.panelBody}><SkelPanel rows={7} head={false} /></div>
        ) : ogeler.length === 0 ? (
          <div className={s.empty}>
            <Inbox size={24} />
            <p className={s.emptyTitle}>Kayıt bulunamadı</p>
            <p className={s.emptyText}>Arama veya filtreleri değiştirmeyi deneyin.</p>
          </div>
        ) : (
          <>
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={`${s.th} ${s.thNo}`}>#</th>
                    <th className={`${s.th} ${s.thSort}`} onClick={() => sirala('code')}>
                      <span className={s.thInner}>Kod<SiraIkon alan="code" /></span>
                    </th>
                    <th className={`${s.th} ${s.thSort}`} onClick={() => sirala('title')}>
                      <span className={s.thInner}>Ünvan<SiraIkon alan="title" /></span>
                    </th>
                    <th className={`${s.th} ${s.num} ${s.thSort}`} onClick={() => sirala('balance')}>
                      <span className={s.thInner}>Net Bakiye<SiraIkon alan="balance" /></span>
                    </th>
                    {DILIMLER.map((d) => (
                      <th key={d.id} className={`${s.th} ${s.num} ${s.thSort}`} onClick={() => sirala(d.sort)}>
                        <span className={s.thInner}>{d.label}<SiraIkon alan={d.sort} /></span>
                      </th>
                    ))}
                    <th className={s.th}>Risk</th>
                    <th className={s.th}>Kısıtlama</th>
                  </tr>
                </thead>
                <tbody>
                  {ogeler.map((m, i) => {
                    const r = RISK_ETIKET[m.risk] || RISK_ETIKET.temiz;
                    const k = m.restriction;
                    return (
                      <tr key={m.code}>
                        <td className={`${s.td} ${s.tdNo}`}>{ilkSira + i + 1}</td>
                        <td className={`${s.td} ${s.code}`}>
                          <Link to={`/v2/admin/customers/${encodeURIComponent(m.code)}`}
                            className={s.codeLink}>{m.code}</Link>
                        </td>
                        <td className={s.td}>
                          <Link to={`/v2/admin/customers/${encodeURIComponent(m.code)}`}
                            className={s.custLink} title={m.title || ''}>{m.title || m.code}</Link>
                        </td>
                        <td className={`${s.td} ${s.num} ${m.balance > 0 ? s.debt : s.credit}`}>
                          {formatTRY(m.balance)}
                        </td>
                        {DILIMLER.map((d) => {
                          const v = Number(m.buckets[d.id]) || 0;
                          return (
                            <td key={d.id} className={`${s.td} ${s.num} ${v > 0 ? s[d.cls + 'Text'] : s.zero}`}>
                              {v > 0 ? formatTRY(v) : '-'}
                            </td>
                          );
                        })}
                        <td className={s.td}><span className={`${s.risk} ${s[r.cls]}`}>{r.label}</span></td>
                        <td className={s.td}>
                          <button type="button"
                            className={`${s.kisitBtn} ${kisitSinif(s, k)}`}
                            onClick={() => setModalMusteri(m)}
                            title={k ? 'Kuralı düzenle' : 'Kısıtlama kuralı ekle'}>
                            {k && k.aktif ? <ShieldAlert size={14} /> : <ShieldCheck size={14} />}
                            {kisitEtiket(k, false)}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ul className={s.mList}>
              {ogeler.map((m) => {
                const r = RISK_ETIKET[m.risk] || RISK_ETIKET.temiz;
                const k = m.restriction;
                return (
                  <li key={`m-${m.code}`} className={s.mItem}>
                    <div className={s.mTop}>
                      <Link to={`/v2/admin/customers/${encodeURIComponent(m.code)}`} className={s.mName}>
                        <span className={s.custLink}>{m.title || m.code}</span>
                        <span className={s.custCode}>{m.code}</span>
                      </Link>
                      <span className={`${s.mBalance} ${m.balance > 0 ? s.debt : s.credit}`}>
                        {formatTRY(m.balance)}
                      </span>
                    </div>
                    <div className={s.mBuckets}>
                      {DILIMLER.map((d) => {
                        const v = Number(m.buckets[d.id]) || 0;
                        if (v <= 0) return null;
                        return (
                          <span key={d.id} className={`${s.mBucket} ${s[d.cls]}`}>
                            {d.id}: {formatTRYShort(v)}
                          </span>
                        );
                      })}
                    </div>
                    <div className={s.mFoot}>
                      <span className={`${s.risk} ${s[r.cls]}`}>{r.label}</span>
                      <button type="button"
                        className={`${s.kisitBtn} ${kisitSinif(s, k)}`}
                        onClick={() => setModalMusteri(m)}>
                        {k && k.aktif ? <ShieldAlert size={14} /> : <ShieldCheck size={14} />}
                        {kisitEtiket(k, true)}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className={s.pager}>
              <span className={s.pagerInfo}>
                {formatNumber(ilkSira + 1)}–{formatNumber(ilkSira + ogeler.length)} / {formatNumber(toplam)}
              </span>
              <label className={s.perBox}>
                <span className={s.fieldLabel}>Sayfa başına</span>
                <select className={s.selectSm} value={params.per}
                  onChange={(e) => guncelle({ per: e.target.value })}>
                  {((data && data.page_sizes) || [25, 50, 100]).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <div className={s.pagerBtns}>
                <button type="button" className={s.pageBtn} disabled={sayfa <= 1 || loading}
                  onClick={() => guncelle({ page: String(sayfa - 1) }, false)} aria-label="Önceki">
                  <ChevronLeft size={16} />
                </button>
                <span className={s.pageNo}>{formatNumber(sayfa)} / {formatNumber(sonSayfa)}</span>
                <button type="button" className={s.pageBtn} disabled={sayfa >= sonSayfa || loading}
                  onClick={() => guncelle({ page: String(sayfa + 1) }, false)} aria-label="Sonraki">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <p className={s.note}>
        <Info size={14} />
        Kısıtlamalar her gece 02:00'de otomatik senkronlanır. Gün içinde el ile
        çalıştırmak için "Senkronize Et" düğmesini kullanın.
      </p>

      <RestrictionModal acik={Boolean(modalMusteri)} musteri={modalMusteri}
        onKapat={() => setModalMusteri(null)} onKaydedildi={kuralKaydedildi} />
      <ToastStack items={toastlar} onDismiss={dismiss} />
    </div>
  );
}
